const AES_KEY_SIZE_BYTES = 32;
const IV_SIZE_BYTES = 12;

const base64ToBytes = (b64: string): Uint8Array => {
  const binary = atob(b64.replace(/\s+/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export const encryptForRecipient = async (
  publicKeyBase64: string,
  data: Uint8Array
): Promise<Uint8Array> => {
  const aesKeyBytes = new Uint8Array(AES_KEY_SIZE_BYTES);
  crypto.getRandomValues(aesKeyBytes);

  const iv = new Uint8Array(IV_SIZE_BYTES);
  crypto.getRandomValues(iv);

  const aesKey = await crypto.subtle.importKey(
    'raw',
    aesKeyBytes.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const cipherBuf = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: 128,
    },
    aesKey,
    data.buffer as ArrayBuffer
  );
  const cipherText = new Uint8Array(cipherBuf);

  const publicKeyDer = base64ToBytes(publicKeyBase64);
  const rsaKey = await crypto.subtle.importKey(
    'spki',
    publicKeyDer.buffer as ArrayBuffer,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );
  const encryptedKeyBuf = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaKey,
    aesKeyBytes.buffer as ArrayBuffer
  );
  const encryptedKeyBytes = new Uint8Array(encryptedKeyBuf);

  const keyLen = encryptedKeyBytes.length;
  if (keyLen > 0xffff) {
    throw new Error('Encrypted key too long');
  }
  const ivLen = iv.length;
  if (ivLen > 0xff) {
    throw new Error('IV too long');
  }

  const out = new Uint8Array(2 + keyLen + 1 + ivLen + cipherText.length);
  let offset = 0;

  out[offset] = (keyLen >>> 8) & 0xff;
  out[offset + 1] = keyLen & 0xff;
  offset += 2;

  out.set(encryptedKeyBytes, offset);
  offset += keyLen;

  out[offset] = ivLen & 0xff;
  offset += 1;

  out.set(iv, offset);
  offset += ivLen;

  out.set(cipherText, offset);

  return out;
};

export const bytesToBase64 = (bytes: Uint8Array): string => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s);
};
